// Pause menu (M5, DESIGN.md §14 "menus"): Esc with nothing to cancel opens a
// pause overlay — resume, save/load, audio volume, tutorial-hints toggle,
// key reference, new game. Opening pauses the sim; closing restores speed.

export interface MenuCallbacks {
  onResume(): void; // also fired when the overlay closes any way
  onSave(name: string): void;
  onLoad(name: string): void;
  onVolume(v: number): void;
  onMuted(m: boolean): void;
  onHints(on: boolean): void;
  onNewGame(): void;
}

const KEYS_HELP = `
  <table class="menu-keys">
    <tr><td>WASD / MMB-drag</td><td>pan camera</td></tr>
    <tr><td>wheel · Q/E</td><td>zoom · rotate</td></tr>
    <tr><td>Space</td><td>pause</td></tr>
    <tr><td>&minus; / =</td><td>game speed</td></tr>
    <tr><td>LMB</td><td>act / select (drag = box-select)</td></tr>
    <tr><td>RMB</td><td>order soldiers (attack-move)</td></tr>
    <tr><td>Ctrl+1..9 / 1..9</td><td>assign / recall control group</td></tr>
    <tr><td>F</td><td>ware-flow overlay</td></tr>
    <tr><td>Esc</td><td>cancel · this menu</td></tr>
  </table>`;

export class PauseMenu {
  private el: HTMLElement;
  private saveNames: string[] = [];
  isOpen = false;

  constructor(
    private parent: HTMLElement,
    private cb: MenuCallbacks,
    private getAudio: () => { volume: number; muted: boolean },
    private getHints: () => boolean,
  ) {
    this.el = document.createElement('div');
    this.el.id = 'menu';
    this.el.style.display = 'none';
    parent.appendChild(this.el);
  }

  setSaves(names: string[]): void {
    this.saveNames = names;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    const audio = this.getAudio();
    this.el.innerHTML = `
      <div class="menu-panel">
        <div class="setup-title">PAUSED</div>
        <button id="menu-resume" class="setup-start">Resume</button>
        <div class="row"><span class="dim">save</span>
          <input id="menu-savename" type="text" value="quicksave" size="12" />
          <button id="menu-save">save</button>
        </div>
        <div class="row"><span class="dim">load</span>
          <select id="menu-loads">
            <option value="">— pick a save —</option>
            ${this.saveNames.map((n) => `<option value="${n}">${n}</option>`).join('')}
          </select>
        </div>
        <div class="row"><span class="dim">volume</span>
          <input id="menu-volume" type="range" min="0" max="100" value="${Math.round(audio.volume * 100)}" />
          <label class="dim"><input id="menu-mute" type="checkbox" ${audio.muted ? 'checked' : ''} /> mute</label>
        </div>
        <div class="row"><span class="dim">tutorial</span>
          <button id="menu-hints">${this.getHints() ? 'hints: on' : 'hints: off'}</button>
        </div>
        ${KEYS_HELP}
        <button id="menu-new">Quit to setup</button>
      </div>`;
    this.el.style.display = 'flex';

    const q = <T extends HTMLElement>(id: string): T => this.el.querySelector<T>(`#${id}`)!;
    q<HTMLButtonElement>('menu-resume').addEventListener('click', () => this.close());
    q<HTMLButtonElement>('menu-save').addEventListener('click', () => {
      const name = q<HTMLInputElement>('menu-savename').value.trim();
      if (name) this.cb.onSave(name);
    });
    q<HTMLSelectElement>('menu-loads').addEventListener('change', (e) => {
      const v = (e.target as HTMLSelectElement).value;
      if (v) {
        this.close();
        this.cb.onLoad(v);
      }
    });
    q<HTMLInputElement>('menu-volume').addEventListener('input', (e) => {
      this.cb.onVolume(Number((e.target as HTMLInputElement).value) / 100);
    });
    q<HTMLInputElement>('menu-mute').addEventListener('change', (e) => {
      this.cb.onMuted((e.target as HTMLInputElement).checked);
    });
    q<HTMLButtonElement>('menu-hints').addEventListener('click', (e) => {
      const on = !this.getHints();
      this.cb.onHints(on);
      (e.target as HTMLButtonElement).textContent = on ? 'hints: on' : 'hints: off';
    });
    q<HTMLButtonElement>('menu-new').addEventListener('click', () => this.cb.onNewGame());
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.el.style.display = 'none';
    this.el.innerHTML = '';
    this.cb.onResume();
  }
}
